import React, { useEffect, useMemo, useState } from "react";
import { Bell, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listUserSubscriptions, adminUpsertSubscription,
  UserAlertSubscription, SubscriptionInput, RuleType,
} from "../api/api";
import { usePartners, useUsers, useBranches } from "../hooks/queries";
import Layout from "../components/Layout";
import { RuleCard, RULE_TYPES, RULE_ROLES, canSubscribeToRuleType, defaultInput, buildVestigingOptions, VestigingOption } from "../components/AlertRuleCard";
import { ROLE_LABELS } from "../lib/roles";
import { cn } from "@/lib/utils";
import { UserAvatar } from "../components/ui/user-avatar";
import { SectionLabel } from "../components/ui/section-label";
import { EmptyState } from "../components/ui/empty-state";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const PARTNER_ROLES = ["hmp", "partner_admin"];

// Rule types are grouped by who may subscribe to them (RULE_ROLES): rules that
// partner roles can receive vs. rules that only exist for Waves roles.
const RULE_GROUPS: { key: string; title: string; description: string; types: RuleType[] }[] = [
  {
    key: "partner",
    title: "Vacaturemeldingen",
    description: "Beschikbaar voor partnerrollen, key account managers en admins.",
    types: RULE_TYPES.filter((rt) => RULE_ROLES[rt].some((r) => PARTNER_ROLES.includes(r))),
  },
  {
    key: "waves",
    title: "Interne meldingen",
    description: "Alleen voor Waves-rollen.",
    types: RULE_TYPES.filter((rt) => !RULE_ROLES[rt].some((r) => PARTNER_ROLES.includes(r))),
  },
];

export default function AlertRegels() {
  const { data: partners = [], isLoading: partnersLoading } = usePartners();
  const { data: users = [] } = useUsers();
  const { data: allBranches = [] } = useBranches(undefined, true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const notifiableRoles = ["hmp", "partner_admin", "kam", "admin"];
  const notifiableUsers = users.filter((u) => notifiableRoles.includes(u.role));

  const partnerMap = new Map(partners.map((p) => [p.id, p]));
  const partnerLabel = (id: number | null) => (id ? partnerMap.get(id)?.name ?? `Partner #${id}` : "Geen partner");

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? notifiableUsers.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    : notifiableUsers;

  // Group users by partner
  const partnerUsers = new Map<number | null, typeof notifiableUsers>();
  for (const u of filteredUsers) {
    const key = u.partner_id ?? null;
    if (!partnerUsers.has(key)) partnerUsers.set(key, []);
    partnerUsers.get(key)!.push(u);
  }

  // Default to the first user once the list has loaded.
  useEffect(() => {
    if (selectedUserId === null && notifiableUsers.length > 0) setSelectedUserId(notifiableUsers[0].id);
  }, [selectedUserId, notifiableUsers]);

  const selectedUser = notifiableUsers.find((u) => u.id === selectedUserId) ?? null;

  // Partner users only ever see their own partner's vestigingen.
  const selectedPartnerId = selectedUser && PARTNER_ROLES.includes(selectedUser.role) ? selectedUser.partner_id ?? null : null;
  const vestigingOptions = useMemo(
    () => buildVestigingOptions(
      selectedPartnerId ? allBranches.filter((b) => b.partner_id === selectedPartnerId) : allBranches,
      partners,
    ),
    [allBranches, partners, selectedPartnerId]
  );

  if (partnersLoading) {
    return (
      <Layout title="Alertering" subtitle="Meldingen per gebruiker beheren">
        <div className="text-sm text-waves-muted">Laden…</div>
      </Layout>
    );
  }

  return (
    <Layout title="Alertering" subtitle="Meldingen per gebruiker beheren">
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        {notifiableUsers.length === 0 ? (
          <div className="rounded-card border border-waves-line bg-white">
            <EmptyState icon={<Bell />} title="Geen gebruikers gevonden" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
            {/* Small screens: user picker as a select */}
            <div className="lg:hidden">
              <Select
                value={selectedUserId !== null ? String(selectedUserId) : undefined}
                onValueChange={(v) => setSelectedUserId(Number(v))}
              >
                <SelectTrigger aria-label="Gebruiker"><SelectValue placeholder="Kies gebruiker" /></SelectTrigger>
                <SelectContent>
                  {notifiableUsers.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name} · {partnerLabel(u.partner_id ?? null)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* lg+: searchable user list grouped by partner */}
            <aside className="hidden flex-col gap-4 rounded-card border border-waves-line bg-white p-4 lg:sticky lg:top-0 lg:flex">
              <div className="flex h-10 items-center gap-2 rounded-pill border border-waves-line bg-white px-4">
                <Search size={15} className="shrink-0 text-waves-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Zoek gebruiker…"
                  className="min-w-0 flex-1 bg-transparent text-sm text-waves-ink outline-none placeholder:text-waves-muted"
                />
              </div>
              <div className="flex max-h-[calc(100vh-280px)] flex-col gap-4 overflow-y-auto">
                {[...partnerUsers.entries()].map(([partnerId, pUsers]) => (
                  <div key={partnerId ?? "none"} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between px-2 pb-1">
                      <SectionLabel>{partnerLabel(partnerId)}</SectionLabel>
                      <span className="font-mono text-[11px] text-waves-muted">{pUsers.length}</span>
                    </div>
                    {pUsers.map((u) => {
                      const active = u.id === selectedUserId;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setSelectedUserId(u.id)}
                          aria-current={active ? "true" : undefined}
                          className={cn(
                            "flex items-center gap-3 rounded-nav px-2 py-2 text-left transition-colors",
                            active ? "bg-waves-mist" : "hover:bg-waves-subtle"
                          )}
                        >
                          <UserAvatar name={u.name} size={32} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-bold text-waves-ink">{u.name}</div>
                            <div className="truncate text-xs text-waves-muted">{ROLE_LABELS[u.role] ?? u.role}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="px-2 py-6 text-center text-sm text-waves-muted">Geen gebruikers gevonden</div>
                )}
              </div>
            </aside>

            <section className="min-w-0">
              {selectedUser ? (
                <UserSubscriptionPanel
                  key={selectedUser.id}
                  user={selectedUser}
                  partnerName={partnerLabel(selectedUser.partner_id ?? null)}
                  partners={partners}
                  vestigingOptions={vestigingOptions}
                />
              ) : (
                <div className="rounded-card border border-waves-line bg-white">
                  <EmptyState icon={<Bell />} title="Kies een gebruiker" description="Selecteer een gebruiker om de meldingen te beheren." />
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </Layout>
  );
}

interface UserPanelProps {
  user: { id: number; name: string; email: string; role: string };
  partnerName: string;
  partners: { id: number; name: string }[];
  vestigingOptions: VestigingOption[];
}

function UserSubscriptionPanel({ user, partnerName, partners, vestigingOptions }: UserPanelProps) {
  const qc = useQueryClient();

  const { data: subs = [], isLoading } = useQuery({
    queryKey: ["user-subscriptions", user.id],
    queryFn:  () => listUserSubscriptions(user.id),
  });

  const upsertMut = useMutation({
    mutationFn: ({ ruleType, body }: { ruleType: RuleType; body: SubscriptionInput }) =>
      adminUpsertSubscription(user.id, ruleType, body),
    onMutate: async ({ ruleType, body }) => {
      const queryKey = ["user-subscriptions", user.id];
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<UserAlertSubscription[]>(queryKey);
      qc.setQueryData<UserAlertSubscription[]>(queryKey, (old = []) => {
        const idx = old.findIndex((s) => s.rule_type === ruleType);
        if (idx === -1) {
          const placeholder: UserAlertSubscription = {
            id: 0, user_id: user.id, rule_type: ruleType,
            enabled: body.enabled, frequency: body.frequency, config: body.config, send_email: body.send_email,
            send_hour: body.send_hour ?? 8, send_minute: body.send_minute ?? 0, send_weekday: body.send_weekday ?? 1,
            created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
          };
          return [...old, placeholder];
        }
        const next = [...old];
        next[idx] = {
          ...next[idx], enabled: body.enabled, frequency: body.frequency, config: body.config, send_email: body.send_email,
          send_hour: body.send_hour ?? next[idx].send_hour,
          send_minute: body.send_minute ?? next[idx].send_minute,
          send_weekday: body.send_weekday ?? next[idx].send_weekday,
        };
        return next;
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(["user-subscriptions", user.id], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["user-subscriptions", user.id] }),
  });

  const subMap = Object.fromEntries(subs.map((s) => [s.rule_type, s])) as
    Partial<Record<RuleType, UserAlertSubscription>>;

  const isKam = user.role === "kam" || user.role === "admin";

  function handleToggle(ruleType: RuleType, enabled: boolean) {
    const existing = subMap[ruleType];
    if (enabled) {
      const input = existing
        ? { enabled: true, frequency: existing.frequency, config: existing.config, send_email: existing.send_email }
        : defaultInput(ruleType);
      upsertMut.mutate({ ruleType, body: input });
    } else if (existing) {
      upsertMut.mutate({ ruleType, body: { ...existing, enabled: false } });
    }
  }

  const groups = RULE_GROUPS
    .map((g) => ({ ...g, types: g.types.filter((rt) => canSubscribeToRuleType(user, rt)) }))
    .filter((g) => g.types.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4 rounded-card border border-waves-line bg-white p-6">
        <UserAvatar name={user.name} size={44} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-black tracking-[-0.01em] text-waves-ink">{user.name}</div>
          <div className="truncate text-sm text-waves-muted">
            {user.email} · {ROLE_LABELS[user.role] ?? user.role} · {partnerName}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-waves-muted">Laden…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-card border border-waves-line bg-white">
          <EmptyState icon={<Bell />} title="Geen meldingen beschikbaar" description="Voor deze rol zijn geen meldingstypes beschikbaar." />
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="flex flex-col gap-3">
            <div>
              <h2 className="text-lg font-black tracking-[-0.01em] text-waves-ink">{g.title}</h2>
              <p className="text-sm text-waves-muted">{g.description}</p>
            </div>
            <div className="flex flex-col gap-2.5">
              {g.types.map((ruleType) => (
                <RuleCard
                  key={ruleType}
                  ruleType={ruleType}
                  sub={subMap[ruleType]}
                  isKam={isKam}
                  partners={partners}
                  vestigingOptions={vestigingOptions}
                  onToggle={(enabled) => handleToggle(ruleType, enabled)}
                  onSave={(body) => upsertMut.mutate({ ruleType, body })}
                  saving={upsertMut.isPending && upsertMut.variables?.ruleType === ruleType}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
